import { useState, useEffect, useCallback } from "react"
import { Check, X, Building2, UserCircle, Loader2, RefreshCw } from "lucide-react"
import AdminSidebar from "../components/AdminSidebar"
import { useLanguage } from "../lib/LanguageContext"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

export default function AdminClubs() {
  const [pending, setPending] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [actionId, setActionId] = useState(null)
  const [error, setError] = useState("")
  const { t } = useLanguage()

  function authHeaders() {
    const token = localStorage.getItem("token")
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
  }

  const fetchPending = useCallback(async () => {
    setIsLoading(true)
    setError("")
    try {
      const res = await fetch(`${API}/api/admin/pending-users`, { headers: authHeaders() })
      if (res.status === 401 || res.status === 403) {
        setError(t("admin_clubs_err_auth"))
        return
      }
      const data = await res.json()
      // Map to display-friendly shape
      setPending(data.map(u => ({
        id: u.id,
        clubName: u.club_name || u.name,
        adminName: u.name,
        email: u.email,
        location: u.location,
        date: u.created_at ? u.created_at.slice(0, 10) : "—",
      })))
    } catch {
      setError(t("admin_clubs_err_conn"))
    } finally {
      setIsLoading(false)
    }
  }, [t])

  useEffect(() => { fetchPending() }, [fetchPending])

  async function handleAction(id, action) {
    setActionId(id)
    try {
      const endpoint = action === "approve"
        ? `${API}/api/admin/users/${id}/approve`
        : `${API}/api/admin/users/${id}/reject`
      const res = await fetch(endpoint, { method: "PUT", headers: authHeaders() })
      if (!res.ok) throw new Error("Action failed")
      setPending(prev => prev.filter(item => item.id !== id))
    } catch {
      setError(t("admin_clubs_err_action"))
    } finally {
      setActionId(null)
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-ink-900 lg:flex-row">
      <AdminSidebar />


      {/* Main Content */}
      <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-10">
        <header className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-paper-0">{t("admin_clubs_title")}</h1>
            <p className="mt-1 text-sm text-mist-300">
              {t("admin_clubs_subtitle")}
            </p>
          </div>
          <button
            onClick={fetchPending}
            className="flex items-center gap-2 rounded-lg border border-white/10 bg-ink-800 px-4 py-2 text-sm font-medium text-mist-300 transition-colors hover:text-paper-0"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t("admin_clubs_refresh")}
          </button>
        </header>

        {error && (
          <div className="mb-6 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>
        )}

        {/* Verification Hierarchy Info */}
        <div className="mb-6">
          <div className="rounded-xl border border-gold-500/20 bg-gold-500/5 px-5 py-4 inline-block">
            <p className="text-xs font-bold uppercase tracking-widest text-gold-400">{t("admin_clubs_verifies")}</p>
            <p className="mt-1 text-lg font-bold text-paper-0">{t("admin_clubs_label")}</p>
          </div>
        </div>

        {/* Approvals Table */}
        <div className="rounded border border-white/10 bg-ink-800/30 shadow-sm">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-gold-400" />
            </div>
          ) : pending.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/5">
                <Check className="h-8 w-8 text-mist-400" />
              </div>
              <h3 className="text-lg font-medium text-paper-0">{t("admin_clubs_caught_up")}</h3>
              <p className="mt-1 text-sm text-mist-400">{t("admin_clubs_no_pending")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-white/10 bg-ink-800/80 text-xs font-semibold uppercase tracking-wider text-mist-400">
                <tr>
                  <th className="px-6 py-4">{t("admin_clubs_col_name")}</th>
                  <th className="px-6 py-4">{t("admin_clubs_col_admin")}</th>
                  <th className="px-6 py-4">{t("admin_clubs_col_loc")}</th>
                  <th className="px-6 py-4">{t("admin_clubs_col_date")}</th>
                  <th className="px-6 py-4 text-right">{t("common_actions")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {pending.map((item) => (
                  <tr key={item.id} className="transition-colors hover:bg-white/[0.02]">
                    <td className="px-6 py-4 font-semibold text-paper-0">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-blue-400" />
                        {item.clubName}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-mist-300">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <UserCircle className="h-4 w-4 text-mist-400" />
                          {item.adminName}
                        </div>
                        <span className="ml-6 text-xs text-mist-500">{item.email}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-mist-400">{item.location || "—"}</td>
                    <td className="px-6 py-4 text-mist-400">{item.date}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleAction(item.id, "reject")}
                          disabled={actionId === item.id}
                          className="flex h-9 w-9 items-center justify-center rounded border border-red-500/30 text-red-400 transition-colors hover:bg-red-500/10 disabled:opacity-50"
                          title={t("admin_clubs_reject")}
                        >
                          {actionId === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => handleAction(item.id, "approve")}
                          disabled={actionId === item.id}
                          className="flex h-9 items-center gap-1.5 rounded bg-gold-500 px-3 text-xs font-semibold text-ink-900 transition-colors hover:bg-gold-400 disabled:opacity-50"
                        >
                          <Check className="h-3.5 w-3.5" />
                          {t("admin_clubs_approve")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
